import React, { useMemo, useState, useRef, useEffect } from "react";
import useSWR from "swr";
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Card, Button, Modal, TextField, MenuItem,
  Select, InputLabel, FormControl, Pagination, Divider, Grid,
  InputAdornment, IconButton, Alert, Snackbar, Switch, List, ListItem,
  ListItemAvatar, Avatar, ListItemText
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import StarIcon from "@mui/icons-material/Star";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { useSelector } from "react-redux";
import { UploadProduk } from "./uploadProduk";
import { QRCodeCanvas } from "qrcode.react";
import { ProductOptionsDialog } from "./ProductOptionsDialog";
import HargaGrosirDialog from "./HargaGrosirDialog";
import { bacaTingkat, ringkasGrosir } from "../utils/hargaGrosir";
import { waktuCabang } from '../utils/waktuCabang';
import { apiHost } from '../utils/apiHost';
import { konfirmasi } from '../utils/dialog';
import { buatQueryEkspor, namaBerkasProduk, pesanGalatEkspor } from '../utils/eksporProduk';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\/+/, "");
  return `${protocol}://${baseUrl}`;
};

const fetcher = (url) => axios.get(url, { withCredentials: true }).then((res) => res.data);

const formatDisplayNumber = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const num = value.toString().replace(/\D/g, "");
  if (num === "") return "";
  return parseInt(num, 10).toLocaleString("id-ID");
};

const CurrencyTextField = ({ label, name, value, onChange, required, helperText, error }) => {
  const [displayValue, setDisplayValue] = useState(formatDisplayNumber(value));

  useEffect(() => {
    setDisplayValue(formatDisplayNumber(value));
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/\./g, "").replace(/[^0-9]/g, "");
    setDisplayValue(raw === "" ? "" : parseInt(raw, 10).toLocaleString("id-ID"));
    onChange({ target: { name, value: raw } });
  };

  return (
    <TextField
      label={label}
      name={name}
      value={displayValue}
      onChange={handleChange}
      fullWidth
      margin="normal"
      required={required}
      helperText={helperText}
      error={error}
      inputProps={{ inputMode: "numeric" }}
    />
  );
};

export const Product = () => {
  const { user } = useSelector((state) => state.auth);

  const [tipeCabang, setTipeCabang] = useState(user?.cabang?.tipe_cabang || "");
  const [namaCabang, setNamaCabang] = useState(user?.cabang?.namacabang || "");

  useEffect(() => {
    if (user?.cabang?.tipe_cabang) {
      setTipeCabang(user.cabang.tipe_cabang);
      setNamaCabang(user.cabang.namacabang || "");
      return;
    }
    if (user?.cabanguuid) {
      axios
        .get(`${getApiBaseUrl()}/cabangbyrole`, { withCredentials: true })
        .then((res) => {
          const list = res.data?.data;
          if (Array.isArray(list) && list.length > 0) {
            setTipeCabang(list[0].tipe_cabang || "");
            setNamaCabang(list[0].namacabang || "");
          } else if (list && !Array.isArray(list)) {
            setTipeCabang(list.tipe_cabang || "");
            setNamaCabang(list.namacabang || "");
          }
        })
        .catch(() => {});
    }
  }, [user]);

  const isRetailCabang = tipeCabang === "retail/toko";

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ kategori: "", sortBy: "terbaru", cabanguuid: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedProductDetail, setSelectedProductDetail] = useState(null);
  const [optionsDialogProduct, setOptionsDialogProduct] = useState(null);
  const [grosirProduct, setGrosirProduct] = useState(null);
  const [favoritDialogOpen, setFavoritDialogOpen] = useState(false);
  const [favoritCabanguuid, setFavoritCabanguuid] = useState("");
  const qrRef = useRef();
  const searchTimeout = useRef(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, message, severity });
  const [mengekspor, setMengekspor] = useState(false);

  const { data: cabangList } = useSWR(
    user?.role === "superadmin" ? `${getApiBaseUrl()}/cabangbyrole?bentuk=objek` : null,
    fetcher
  );
  const cabangOptions = cabangList?.data || [];

  const productsEndpoint = useMemo(() => {
    const params = new URLSearchParams({
      page: String(currentPage),
      limit: String(itemsPerPage),
    });
    if (filters.kategori) params.append("kategoriuuid", filters.kategori);
    if (filters.sortBy === "terbaru") params.append("sort", "createdAt DESC");
    else if (filters.sortBy === "terlama") params.append("sort", "createdAt ASC");
    if (search) params.append("search", search);
    if (user?.role === "superadmin" && filters.cabanguuid) params.append("cabanguuid", filters.cabanguuid);
    return `${getApiBaseUrl()}/backoffice/barang-paginate?${params.toString()}`;
  }, [currentPage, itemsPerPage, filters.kategori, filters.sortBy, filters.cabanguuid, search, user?.role]);

  const { data: productsData, error: productError, mutate } = useSWR(productsEndpoint, fetcher);

  const effectiveFavoritCabanguuid = user?.role === "admin" ? user?.cabanguuid : favoritCabanguuid;
  const favoritEndpoint = favoritDialogOpen && effectiveFavoritCabanguuid
    ? `${getApiBaseUrl()}/barangcabang-favorit?cabanguuid=${effectiveFavoritCabanguuid}`
    : null;
  const { data: favoritData, mutate: mutateFavorit } = useSWR(favoritEndpoint, fetcher);
  const favoritList = favoritData?.data || [];

  const downloadExcel = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/download-template-excel`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template_barang.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Gagal mengunduh template Excel", "error");
    }
  };

  // Ekspor SEMUA produk yang cocok dengan filter yang sedang dipakai (bukan hanya halaman ini); dibuat server, tercatat di Log Aktivitas.
  const eksporExcel = async () => {
    setMengekspor(true);
    try {
      const query = buatQueryEkspor({ search, kategori: filters.kategori, cabanguuid: filters.cabanguuid, role: user?.role });
      const response = await axios.get(`${getApiBaseUrl()}/export-produk${query}`, { withCredentials: true, responseType: "blob" });
      const cabangDipilih = cabangOptions.find((c) => c.uuid === filters.cabanguuid);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", namaBerkasProduk(user?.role === "admin" ? namaCabang : cabangDipilih?.namacabang));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showSnackbar(await pesanGalatEkspor(error, "Gagal mengekspor produk"), "error");
    } finally {
      setMengekspor(false);
    }
  };

  const { data: categories } = useSWR(`${getApiBaseUrl()}/kategori`, fetcher);
  const categoryList = categories?.data || [];

  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    namabarang: "",
    kodebarang: "",
    harga: "",
    harga_modal: "",
    kategoriuuid: "",
    stokCabang: "",
    cabanguuid: "",
    file: null,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const filteredCategoryList = useMemo(() => {
    if (user?.role !== "superadmin") return categoryList;
    if (!formData.cabanguuid) return [];
    return categoryList.filter(
      (cat) => cat.cabanguuid === formData.cabanguuid || cat.cabanguuid === null || cat.cabanguuid === undefined || cat.cabanguuid === ""
    );
  }, [categoryList, formData.cabanguuid, user?.role]);

  const products = useMemo(() => {
    const rows = productsData?.data || [];
    if (!Array.isArray(rows)) return [];
    if (user?.role === "admin") {
      return rows
        .filter((item) => item && item.Barang)
        .map((item) => ({
          uuid: item.Barang?.uuid || "",
          namabarang: item.Barang?.namabarang || "",
          kodebarang: item.Barang?.kodebarang || "",
          harga: item.Barang?.harga || 0,
          harga_modal: item.Barang?.harga_modal || null,
          foto: item.Barang?.foto || "",
          kategoriuuid: item.Barang?.kategoriuuid || "",
          Kategori: item.Barang?.Kategori || null,
          createdAt: item.Barang?.createdAt || null,
          baranguuid: item?.baranguuid || "",
          cabanguuid: item?.cabanguuid || "",
          stokCabang: item?.stok || 0,
          hargaGrosir: bacaTingkat(item?.hargaGrosir),
          tampilFavorit: !!item?.tampilFavorit,
          Cabang: item?.Cabang || null,
        }));
    }
    if (user?.role === "superadmin") {
      return rows
        .filter((item) => item && item.Barang)
        .map((item) => ({
          uuid: item.Barang?.uuid || "",
          namabarang: item.Barang?.namabarang || "",
          kodebarang: item.Barang?.kodebarang || "",
          harga: item.Barang?.harga || 0,
          harga_modal: item.Barang?.harga_modal || null,
          foto: item.Barang?.foto || "",
          kategoriuuid: item.Barang?.kategoriuuid || "",
          Kategori: item.Barang?.Kategori || null,
          createdAt: item.Barang?.createdAt || null,
          baranguuid: item?.baranguuid || "",
          cabanguuid: item?.cabanguuid || "",
          stokCabang: item?.stok || 0,
          hargaGrosir: bacaTingkat(item?.hargaGrosir),
          tampilFavorit: !!item?.tampilFavorit,
          Cabang: item?.Cabang || null,
        }));
    }
    return rows.filter((item) => item?.uuid).map((item) => ({ ...item, Cabang: null }));
  }, [productsData, user?.role]);

  const totalPages = productsData?.pagination?.totalPages || 1;

  const handlePageChange = (event, value) => setCurrentPage(value);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearchInputChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearch(val);
      setCurrentPage(1);
    }, 400);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setCurrentPage(1);
  };

  const handleOpenDetail = (product) => {
    setSelectedProductDetail(product);
    setOpenDetail(true);
  };

  const handleCloseDetail = () => {
    setOpenDetail(false);
    setSelectedProductDetail(null);
  };

  const downloadQRCode = () => {
    if (!selectedProductDetail.kodebarang) {
      showSnackbar("Kode produk belum diisi, tidak bisa download QR code", "error");
      return;
    }
    if (qrRef.current) {
      const canvas = qrRef.current.querySelector("canvas");
      const url = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `QR-${selectedProductDetail.namabarang}-${selectedProductDetail.kodebarang}.png`;
      link.href = url;
      link.click();
    }
  };

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setFormData({ namabarang: "", kodebarang: "", harga: "", kategoriuuid: "", stokCabang: "", harga_modal: "", cabanguuid: "", file: null });
    setIsEditing(false);
    setEditId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "cabanguuid") {
      setFormData((prev) => ({ ...prev, cabanguuid: value, kategoriuuid: "" }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, file: e.target.files[0] }));
  };

  const handleSubmit = async () => {
    if (!formData.namabarang.trim()) {
      showSnackbar("Nama Barang harus diisi.", "error");
      return;
    }
    if (!formData.kategoriuuid) {
      showSnackbar("Kategori harus dipilih.", "error");
      return;
    }
    if (user?.role === "superadmin" && !formData.cabanguuid) {
      showSnackbar("Cabang harus dipilih.", "error");
      return;
    }
    if (user?.role === "admin" && (formData.stokCabang === "" || formData.stokCabang === null || formData.stokCabang === undefined)) {
      showSnackbar("Stok Cabang harus diisi.", "error");
      return;
    }
    const stokValue = parseInt(formData.stokCabang, 10);
    if (user?.role === "admin" && (isNaN(stokValue) || stokValue < 0 || stokValue > 2147483647)) {
      showSnackbar("Stok Cabang tidak valid. Maksimal 2.147.483.647.", "error");
      return;
    }
    const formDataToSend = new FormData();
    formDataToSend.append("namabarang", formData.namabarang);
    formDataToSend.append("kodebarang", formData.kodebarang?.trim() || "");
    formDataToSend.append("harga", formData.harga !== "" ? formData.harga : 0);
    formDataToSend.append("kategoriuuid", formData.kategoriuuid);
    formDataToSend.append("harga_modal", formData.harga_modal !== "" ? formData.harga_modal : 0);
    if (user?.role === "admin") {
      formDataToSend.append("stokCabang", formData.stokCabang || 0);
    }
    if (user?.role === "superadmin") {
      formDataToSend.append("cabanguuid", formData.cabanguuid);
      formDataToSend.append("stokCabang", formData.stokCabang || 0);
    }
    if (formData.file) formDataToSend.append("file", formData.file);

    try {
      if (isEditing) {
        const updateUrl =
          user?.role === "admin"
            ? `${getApiBaseUrl()}/updateBarangCabangByAdmin/${editId}`
            : `${getApiBaseUrl()}/updatebarang/${editId}`;
        await axios.put(updateUrl, formDataToSend, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
        showSnackbar("Produk berhasil diperbarui");
      } else {
        const createUrl =
          user?.role === "admin"
            ? `${getApiBaseUrl()}/createbarangcabangbyadmin`
            : `${getApiBaseUrl()}/createbarang`;
        const res = await axios.post(createUrl, formDataToSend, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
        const msg = res.data?.message || "Produk berhasil ditambahkan";
        showSnackbar(msg);
      }
      mutate();
      handleClose();
    } catch (error) {
      const errorMsg = error.response?.data?.msg || error.response?.data?.message || "Terjadi kesalahan saat menyimpan barang";
      showSnackbar(errorMsg, "error");
    }
  };

  const handleEdit = (product) => {
    setFormData({
      namabarang: product.namabarang,
      kodebarang: product.kodebarang || "",
      harga: product.harga ? String(parseFloat(product.harga)) : "",
      harga_modal: product.harga_modal ? String(parseFloat(product.harga_modal)) : "",
      kategoriuuid: product.kategoriuuid,
      stokCabang: product.stokCabang || "",
      cabanguuid: product.cabanguuid || "",
      file: null,
    });
    setEditId(product.uuid);
    setIsEditing(true);
    handleOpen();
  };

  const handleDelete = async (product) => {
    if (!await konfirmasi("Apakah Anda yakin ingin menghapus barang ini? Tindakan ini tidak dapat dibatalkan.")) return;
    try {
      if (user?.role === "admin") {
        await axios.delete(`${getApiBaseUrl()}/deletebarangbyadmin/${product.uuid}`, { withCredentials: true });
      } else {
        await axios.delete(
          `${getApiBaseUrl()}/deletebarang/${product.uuid}?cabanguuid=${product.cabanguuid}`,
          { withCredentials: true }
        );
      }
      mutate();
      showSnackbar("Produk berhasil dihapus");
    } catch (error) {
      showSnackbar(error.response?.data?.message || error.response?.data?.msg || "Gagal menghapus barang", "error");
    }
  };

  const handleToggleFavorit = async (product) => {
    try {
      await axios.put(
        `${getApiBaseUrl()}/updatebarangcabang/${product.baranguuid}`,
        { baranguuid: product.baranguuid, cabanguuid: product.cabanguuid, tampilFavorit: !product.tampilFavorit },
        { withCredentials: true }
      );
      mutate();
      mutateFavorit();
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Gagal mengubah status favorit", "error");
    }
  };

  const handleMoveFavorit = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= favoritList.length) return;
    const a = favoritList[index];
    const b = favoritList[targetIndex];
    try {
      await Promise.all([
        axios.put(`${getApiBaseUrl()}/updatebarangcabang/${a.baranguuid}`,
          { baranguuid: a.baranguuid, cabanguuid: a.cabanguuid, urutanFavorit: b.urutanFavorit },
          { withCredentials: true }),
        axios.put(`${getApiBaseUrl()}/updatebarangcabang/${b.baranguuid}`,
          { baranguuid: b.baranguuid, cabanguuid: b.cabanguuid, urutanFavorit: a.urutanFavorit },
          { withCredentials: true }),
      ]);
      mutateFavorit();
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Gagal mengubah urutan favorit", "error");
    }
  };

  const handleRemoveFavorit = async (item) => {
    try {
      await axios.put(
        `${getApiBaseUrl()}/updatebarangcabang/${item.baranguuid}`,
        { baranguuid: item.baranguuid, cabanguuid: item.cabanguuid, tampilFavorit: false },
        { withCredentials: true }
      );
      mutate();
      mutateFavorit();
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Gagal menghapus dari favorit", "error");
    }
  };

  if (productError) return <Typography color="error">Failed to load data: {productError.message}</Typography>;
  if (!productsData) return <Typography>Loading...</Typography>;

  const showCabangColumn = user?.role === "superadmin";


  return (
    <Box sx={{ padding: 2, overflowX: "auto" }}>
      <Typography variant="h6" gutterBottom>
        Product List
        {user?.role === "admin" && (
          <Typography variant="subtitle2" color="text.secondary">
            Cabang: {namaCabang} {tipeCabang ? `(${tipeCabang})` : ""}
          </Typography>
        )}
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "stretch", gap: 2, mb: 2 }}>
        {(user?.role === "superadmin" || user?.role === "admin") && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
              flex: { xs: "1 1 100%", md: "1 1 auto" },
              minWidth: 0,
            }}
          >
            <Button variant="contained" color="primary" onClick={handleOpen} sx={{ flexShrink: 0 }}>
              Add Product
            </Button>
            <UploadProduk inline />
            <Button variant="outlined" color="primary" onClick={downloadExcel} sx={{ flexShrink: 0 }}>
              Unduh Template Excel
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={eksporExcel}
              disabled={mengekspor}
              sx={{ flexShrink: 0 }}
            >
              {mengekspor ? "Mengekspor..." : "Ekspor Excel"}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<StarIcon />}
              onClick={() => {
                if (user?.role === "admin") setFavoritCabanguuid(user?.cabanguuid || "");
                setFavoritDialogOpen(true);
              }}
              sx={{ flexShrink: 0 }}
            >
              Kelola Menu Favorit
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2, alignItems: "center" }}>
        <TextField
          placeholder="Cari nama / kode barang..."
          value={searchInput}
          onChange={handleSearchInputChange}
          size="small"
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Filter Kategori</InputLabel>
          <Select name="kategori" value={filters.kategori} onChange={handleFilterChange} label="Filter Kategori">
            <MenuItem value="">Semua Kategori</MenuItem>
            {categoryList.map((category) => (
              <MenuItem key={category.uuid} value={category.uuid}>
                {category.namakategori}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {user?.role === "superadmin" && (
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Filter Cabang</InputLabel>
            <Select name="cabanguuid" value={filters.cabanguuid} onChange={handleFilterChange} label="Filter Cabang">
              <MenuItem value="">Semua Cabang</MenuItem>
              {cabangOptions.map((c) => (
                <MenuItem key={c.uuid} value={c.uuid}>
                  {c.namacabang}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl sx={{ minWidth: 160 }} size="small">
          <InputLabel>Urutkan</InputLabel>
          <Select name="sortBy" value={filters.sortBy} onChange={handleFilterChange} label="Urutkan">
            <MenuItem value="terbaru">Terbaru</MenuItem>
            <MenuItem value="terlama">Terlama</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Card>
        <Box sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>No</TableCell>
                <TableCell>Nama Barang</TableCell>
                <TableCell>Kode Barang</TableCell>
                <TableCell>Harga</TableCell>
                {(user?.role === "superadmin" || user?.role === "admin") && <TableCell>Harga Modal</TableCell>}
                <TableCell>Kategori</TableCell>
                {user?.role === "admin" && <TableCell>Stok Cabang</TableCell>}
                {showCabangColumn && <TableCell>Cabang</TableCell>}
                {showCabangColumn && <TableCell>Stok</TableCell>}
                {(user?.role === "superadmin" || user?.role === "admin") && <TableCell>Favorit</TableCell>}
                <TableCell>Foto</TableCell>
                <TableCell>Tanggal Dibuat</TableCell>
                {(user?.role === "superadmin" || user?.role === "admin") && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Tidak ada data barang
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product, index) => (
                  <TableRow key={`${product.uuid}-${product.cabanguuid || index}`}>
                    <TableCell>{(currentPage - 1) * itemsPerPage + index + 1}</TableCell>
                    <TableCell>{product.namabarang}</TableCell>
                    <TableCell>{product.kodebarang || "-"}</TableCell>
                    <TableCell>
                      Rp {parseFloat(product.harga || 0).toLocaleString("id-ID")}
                      {ringkasGrosir(product.hargaGrosir) && (
                        <Typography variant="caption" color="success.main" display="block">Grosir: {ringkasGrosir(product.hargaGrosir)}</Typography>
                      )}
                    </TableCell>
                    {(user?.role === "superadmin" || user?.role === "admin") && (
                      <TableCell>
                        {product.harga_modal ? `Rp ${parseFloat(product.harga_modal).toLocaleString("id-ID")}` : "-"}
                      </TableCell>
                    )}
                    <TableCell>{product.Kategori?.namakategori || "-"}</TableCell>
                    {user?.role === "admin" && <TableCell>{product.stokCabang || 0}</TableCell>}
                    {showCabangColumn && (
                      <TableCell>
                        <Typography variant="body2">{product.Cabang?.namacabang || "-"}</Typography>
                        {product.Cabang?.tipe_cabang && (
                          <Typography variant="caption" color="text.secondary">
                            {product.Cabang.tipe_cabang}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {showCabangColumn && <TableCell>{product.stokCabang ?? "-"}</TableCell>}
                    {(user?.role === "superadmin" || user?.role === "admin") && (
                      <TableCell>
                        <Switch
                          checked={!!product.tampilFavorit}
                          onChange={() => handleToggleFavorit(product)}
                          color="warning"
                          size="small"
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      {product.foto ? (
                        <img
                          src={`${getApiBaseUrl()}/uploads/${product.foto}`}
                          alt={product.namabarang}
                          style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            bgcolor: "grey.200",
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography variant="caption" color="text.disabled">
                            No foto
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{product.createdAt ? waktuCabang(product.createdAt).toLocaleString("id-ID") : "-"}</TableCell>
                    {(user?.role === "superadmin" || user?.role === "admin") && (
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1, flexDirection: "column", alignItems: "flex-start" }}>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button size="small" variant="outlined" color="info" onClick={() => handleOpenDetail(product)}>
                              Detail
                            </Button>
                            <Button size="small" variant="outlined" color="primary" onClick={() => handleEdit(product)}>
                              Edit
                            </Button>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={() => setOptionsDialogProduct(product)}
                          >
                            Addon
                          </Button>
                          <Button size="small" variant="outlined" color="success" onClick={() => setGrosirProduct(product)}>
                            Grosir
                          </Button>
                          <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(product)}>
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Card>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" />
        </Box>
      )}

      <Modal open={openDetail} onClose={handleCloseDetail}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            maxHeight: "90vh",
            overflowY: "auto",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Detail Produk
          </Typography>
          {selectedProductDetail && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Nama Barang
                  </Typography>
                  <Typography variant="body1">{selectedProductDetail.namabarang}</Typography>
                </Grid>
                {isRetailCabang && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Kode Barang
                    </Typography>
                    <Typography variant="body1">{selectedProductDetail.kodebarang || "-"}</Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Harga
                  </Typography>
                  <Typography variant="body1">
                    Rp {parseFloat(selectedProductDetail.harga || 0).toLocaleString("id-ID")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Kategori
                  </Typography>
                  <Typography variant="body1">{selectedProductDetail.Kategori?.namakategori || "-"}</Typography>
                </Grid>
                {user?.role === "admin" && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Stok Cabang
                    </Typography>
                    <Typography variant="body1">{selectedProductDetail.stokCabang || 0}</Typography>
                  </Grid>
                )}
                {showCabangColumn && selectedProductDetail.Cabang && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Cabang
                    </Typography>
                    <Typography variant="body1">{selectedProductDetail.Cabang.namacabang}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedProductDetail.Cabang.tipe_cabang}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                QR Code Produk
              </Typography>
              {selectedProductDetail.kodebarang ? (
                <>
                  <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                    <Box ref={qrRef}>
                      <QRCodeCanvas value={selectedProductDetail.kodebarang} size={200} level="H" includeMargin={true} />
                    </Box>
                  </Box>
                  <Button variant="contained" color="primary" fullWidth onClick={downloadQRCode} sx={{ mb: 2 }}>
                    Download QR Code
                  </Button>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                  Kode produk belum diisi, QR code tidak dapat ditampilkan
                </Typography>
              )}
              <Button variant="outlined" color="secondary" fullWidth onClick={handleCloseDetail}>
                Tutup
              </Button>
            </Box>
          )}
        </Box>
      </Modal>

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 420,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            maxHeight: "90vh",
            overflowY: "auto",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {isEditing ? "Edit Product" : "Add Product"}
          </Typography>

          {user?.role === "superadmin" && !isEditing && (
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Pilih Cabang *</InputLabel>
              <Select name="cabanguuid" value={formData.cabanguuid} onChange={handleChange} label="Pilih Cabang *">
                <MenuItem value="">-- Pilih Cabang --</MenuItem>
                {cabangOptions.map((c) => (
                  <MenuItem key={c.uuid} value={c.uuid}>
                    {c.namacabang} {c.tipe_cabang ? `(${c.tipe_cabang})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {user?.role === "superadmin" && !isEditing && !formData.cabanguuid && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Pilih cabang terlebih dahulu untuk melihat kategori yang tersedia.
            </Alert>
          )}

          <TextField
            label="Nama Barang *"
            name="namabarang"
            value={formData.namabarang}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />

          <CurrencyTextField
            label="Harga"
            name="harga"
            value={formData.harga}
            onChange={handleChange}
            helperText="Kosongkan atau isi 0 jika belum ada harga"
          />

          <TextField
            label="Kode Barang (SKU)"
            name="kodebarang"
            value={formData.kodebarang}
            onChange={handleChange}
            fullWidth
            margin="normal"
            helperText="Kosongkan untuk dibuat otomatis (BRG-000001, ...). Bisa diganti kapan saja."
          />

          <CurrencyTextField
            label="Harga Modal (HPP)"
            name="harga_modal"
            value={formData.harga_modal}
            onChange={handleChange}
            helperText="Kosongkan atau isi 0 jika tidak ada"
          />

          <FormControl
            fullWidth
            margin="normal"
            required
            disabled={user?.role === "superadmin" && !formData.cabanguuid}
          >
            <InputLabel>Kategori *</InputLabel>
            <Select name="kategoriuuid" value={formData.kategoriuuid} onChange={handleChange} label="Kategori *">
              {user?.role === "superadmin" && !formData.cabanguuid ? (
                <MenuItem value="" disabled>Pilih cabang terlebih dahulu</MenuItem>
              ) : (
                filteredCategoryList.map((category) => (
                  <MenuItem key={category.uuid} value={category.uuid}>
                    {category.namakategori}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {user?.role === "superadmin" && formData.cabanguuid && filteredCategoryList.length === 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Belum ada kategori untuk cabang ini.
            </Alert>
          )}

          {(user?.role === "admin" || user?.role === "superadmin") && (
            <TextField
              label="Stok Cabang"
              name="stokCabang"
              type="number"
              value={formData.stokCabang}
              onChange={handleChange}
              fullWidth
              margin="normal"
              helperText={user?.role === "admin" ? "Masukkan jumlah stok untuk cabang Anda" : "Masukkan jumlah stok awal untuk cabang yang dipilih"}
              inputProps={{ min: 0 }}
            />
          )}

          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Upload Foto (Opsional)
            </Typography>
            <input type="file" onChange={handleFileChange} accept=".png,.jpg,.jpeg" style={{ marginTop: "8px" }} />
            <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
              Format: PNG, JPG, JPEG (Max 5MB)
            </Typography>
          </Box>

          {user?.role === "superadmin" && !isEditing && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Jika nama barang sudah ada, barang akan langsung ditambahkan ke stok cabang yang dipilih.
            </Alert>
          )}

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button variant="outlined" color="secondary" onClick={handleClose} fullWidth>
              Batal
            </Button>
            <Button variant="contained" color="primary" onClick={handleSubmit} fullWidth>
              {isEditing ? "Update" : "Simpan"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={favoritDialogOpen} onClose={() => setFavoritDialogOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 480,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            maxHeight: "90vh",
            overflowY: "auto",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Kelola Menu Favorit
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Produk di sini akan tampil di bagian "Menu Favorit" pada halaman Web Menu cabang, sesuai urutan di bawah.
            Aktifkan toggle "Favorit" di tabel produk untuk menambahkan produk baru ke sini.
          </Typography>

          {user?.role === "superadmin" && (
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel>Pilih Cabang</InputLabel>
              <Select
                value={favoritCabanguuid}
                onChange={(e) => setFavoritCabanguuid(e.target.value)}
                label="Pilih Cabang"
              >
                <MenuItem value="">-- Pilih Cabang --</MenuItem>
                {cabangOptions.map((c) => (
                  <MenuItem key={c.uuid} value={c.uuid}>
                    {c.namacabang}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {user?.role === "superadmin" && !favoritCabanguuid ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Pilih cabang terlebih dahulu untuk mengatur menu favoritnya.
            </Alert>
          ) : favoritList.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Belum ada produk favorit. Aktifkan toggle "Favorit" di tabel produk untuk menambahkannya.
            </Alert>
          ) : (
            <List sx={{ mt: 1 }}>
              {favoritList.map((item, index) => (
                <ListItem
                  key={item.baranguuid}
                  divider
                  secondaryAction={
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        size="small"
                        disabled={index === 0}
                        onClick={() => handleMoveFavorit(index, -1)}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={index === favoritList.length - 1}
                        onClick={() => handleMoveFavorit(index, 1)}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleRemoveFavorit(item)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      variant="rounded"
                      src={item.Barang?.foto ? `${getApiBaseUrl()}/uploads/${item.Barang.foto}` : undefined}
                    >
                      {item.Barang?.namabarang?.[0] || "?"}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.Barang?.namabarang || "-"}
                    secondary={
                      item.Barang?.Kategori?.tampilDiMenu === false
                        ? `Rp ${parseFloat(item.Barang?.harga || 0).toLocaleString("id-ID")} · Tidak tampil di Web Menu (kategori "${item.Barang?.Kategori?.namakategori || "-"}" disembunyikan)`
                        : `Rp ${parseFloat(item.Barang?.harga || 0).toLocaleString("id-ID")}`
                    }
                    secondaryTypographyProps={
                      item.Barang?.Kategori?.tampilDiMenu === false ? { color: "warning.main" } : undefined
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            sx={{ mt: 3 }}
            onClick={() => setFavoritDialogOpen(false)}
          >
            Tutup
          </Button>
        </Box>
      </Modal>

      <HargaGrosirDialog
        product={grosirProduct}
        superadmin={user?.role === "superadmin"}
        onClose={() => setGrosirProduct(null)}
        onSaved={() => { mutate(); showSnackbar("Harga grosir disimpan", "success"); }}
      />

      <ProductOptionsDialog
        open={Boolean(optionsDialogProduct)}
        onClose={() => setOptionsDialogProduct(null)}
        baranguuid={optionsDialogProduct?.uuid}
        namabarang={optionsDialogProduct?.namabarang}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};